import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

export interface RecommendationJob {
  id: string;
  videoName: string;
  categoryId: string;
  categoryName: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress: number;
  startedAt: Date;
  completedAt?: Date;
  result?: any;
  error?: string;
}

interface RecommendationContextType {
  jobs: RecommendationJob[];
  activeJobsCount: number;
  addJob: (job: Omit<RecommendationJob, 'id' | 'startedAt' | 'progress' | 'status'>) => string;
  updateJob: (jobId: string, updates: Partial<RecommendationJob>) => void;
  removeJob: (jobId: string) => void;
  getJob: (jobId: string) => RecommendationJob | undefined;
  clearCompletedJobs: () => void;
}

const RecommendationContext = createContext<RecommendationContextType | undefined>(undefined);

export const RecommendationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [jobs, setJobs] = useState<RecommendationJob[]>([]);

  const activeJobsCount = jobs.filter(job => 
    job.status === 'pending' || job.status === 'processing'
  ).length;

  const addJob = useCallback((
    jobData: Omit<RecommendationJob, 'id' | 'startedAt' | 'progress' | 'status'>
  ): string => {
    // Check if a similar job already exists (same video, same category, and is pending or processing)
    const existingJob = jobs.find(job => 
      job.videoName === jobData.videoName && 
      job.categoryId === jobData.categoryId &&
      (job.status === 'pending' || job.status === 'processing')
    );

    // If a similar job exists and is still active, return its ID instead of creating a duplicate
    if (existingJob) {
      console.log('⚠️ Prevented duplicate job creation:', existingJob.id);
      return existingJob.id;
    }

    const jobId = `rec_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const newJob: RecommendationJob = {
      ...jobData,
      id: jobId,
      status: 'pending',
      progress: 0,
      startedAt: new Date(),
    };
    
    setJobs(prev => [newJob, ...prev]);
    return jobId;
  }, [jobs]);

  const updateJob = useCallback((jobId: string, updates: Partial<RecommendationJob>) => {
    setJobs(prev => prev.map(job => 
      job.id === jobId ? { ...job, ...updates } : job
    ));
  }, []);

  const removeJob = useCallback((jobId: string) => {
    setJobs(prev => prev.filter(job => job.id !== jobId));
  }, []);

  const getJob = useCallback((jobId: string) => {
    return jobs.find(job => job.id === jobId);
  }, [jobs]);

  const clearCompletedJobs = useCallback(() => {
    setJobs(prev => prev.filter(job => 
      job.status !== 'completed' && job.status !== 'failed'
    ));
  }, []);

  // Auto-cleanup old completed/failed jobs after 5 minutes
  useEffect(() => {
    const interval = setInterval(() => {
      const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
      setJobs(prev => prev.filter(job => {
        if (job.status === 'completed' || job.status === 'failed') {
          return job.completedAt && job.completedAt > fiveMinutesAgo;
        }
        return true;
      }));
    }, 60000); // Check every minute

    return () => clearInterval(interval);
  }, []);

  const value: RecommendationContextType = {
    jobs,
    activeJobsCount,
    addJob,
    updateJob,
    removeJob,
    getJob,
    clearCompletedJobs,
  };

  return (
    <RecommendationContext.Provider value={value}>
      {children}
    </RecommendationContext.Provider>
  );
};

export const useRecommendations = () => {
  const context = useContext(RecommendationContext);
  if (!context) {
    throw new Error('useRecommendations must be used within RecommendationProvider');
  }
  return context;
};

